/**
 * TarefasPage.tsx — piloto da migração (F3): réplica visual do
 * templates/tasks.html original (cards brancos rounded-xl shadow-md,
 * título+subtítulo, linhas com checkbox e hover indigo).
 */

import { faPlus, faTrashAlt } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useState, type FormEvent } from 'react'
import {
  criarLembrete,
  criarTarefa,
  definirConclusao,
  excluirLembrete,
  excluirTarefa,
  listarLembretes,
  listarTarefas,
  marcarLembreteLido,
  type Tarefa,
} from '../../api/tarefas'
import { useToast } from '../../components/Toast'

function CardBranco({ titulo, children }: { titulo: string; children: React.ReactNode }) {
  return (
    <div className="mb-8 rounded-xl bg-white p-6 shadow-md dark:bg-dark-card">
      <h2 className="mb-4 text-xl font-bold text-slate-800 dark:text-slate-100">{titulo}</h2>
      {children}
    </div>
  )
}

function LinhaTarefa({ tarefa }: { tarefa: Tarefa }) {
  const clienteQuery = useQueryClient()
  const { notificar } = useToast()
  const invalidar = () => clienteQuery.invalidateQueries({ queryKey: ['tarefas'] })

  const alternar = useMutation({
    mutationFn: () => definirConclusao(tarefa.id, !tarefa.concluida),
    onSuccess: invalidar,
    onError: () => notificar('error', 'Erro ao atualizar a tarefa.'),
  })
  const excluir = useMutation({
    mutationFn: () => excluirTarefa(tarefa.id),
    onSuccess: () => {
      void invalidar()
      notificar('success', 'Tarefa excluída.')
    },
    onError: () => notificar('error', 'Erro ao excluir a tarefa.'),
  })

  return (
    <li
      className={`flex items-center justify-between rounded-lg border p-3 transition-colors dark:border-dark-border ${
        tarefa.concluida
          ? 'bg-slate-50 dark:bg-dark-bg'
          : 'hover:bg-indigo-50 dark:hover:bg-dark-hover'
      }`}
    >
      <div className="flex items-center">
        <input
          type="checkbox"
          checked={tarefa.concluida}
          onChange={() => alternar.mutate()}
          disabled={alternar.isPending}
          aria-label={`Concluir ${tarefa.titulo}`}
          className="mr-4 h-5 w-5 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
        />
        <div>
          <span
            className={
              tarefa.concluida
                ? 'text-slate-500 line-through'
                : 'text-slate-800 dark:text-slate-100'
            }
          >
            {tarefa.titulo}
          </span>
          {tarefa.objeto && (
            <p className="text-xs text-slate-500 dark:text-slate-400">
              Vinculado ao processo: <span className="text-indigo-600">{tarefa.objeto}</span>
            </p>
          )}
        </div>
      </div>
      <button
        onClick={() => excluir.mutate()}
        disabled={excluir.isPending}
        className={
          tarefa.concluida
            ? 'text-gray-400 hover:text-red-600'
            : 'text-red-500 hover:text-red-700'
        }
        aria-label={`Excluir ${tarefa.titulo}`}
      >
        <FontAwesomeIcon icon={faTrashAlt} />
      </button>
    </li>
  )
}

function SecaoTarefas() {
  const { notificar } = useToast()
  const clienteQuery = useQueryClient()
  const [titulo, setTitulo] = useState('')

  const consulta = useQuery({ queryKey: ['tarefas'], queryFn: listarTarefas })
  const criar = useMutation({
    mutationFn: () => criarTarefa(titulo.trim()),
    onSuccess: () => {
      setTitulo('')
      void clienteQuery.invalidateQueries({ queryKey: ['tarefas'] })
      notificar('success', 'Nova tarefa adicionada!')
    },
    onError: () => notificar('error', 'Erro ao criar a tarefa.'),
  })

  function aoEnviar(e: FormEvent) {
    e.preventDefault()
    if (titulo.trim()) criar.mutate()
  }

  return (
    <>
      <div className="mb-8 rounded-xl bg-white p-6 shadow-md dark:bg-dark-card">
        <form onSubmit={aoEnviar} className="flex items-center space-x-4">
          <input
            value={titulo}
            onChange={(e) => setTitulo(e.target.value)}
            placeholder="Nova tarefa…"
            className="flex-grow rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
          />
          <button
            type="submit"
            disabled={criar.isPending || !titulo.trim()}
            className="rounded-md bg-indigo-600 px-4 py-2 font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            <FontAwesomeIcon icon={faPlus} className="mr-2" />
            Adicionar
          </button>
        </form>
      </div>

      <CardBranco titulo="Pendentes">
        <ul className="space-y-3">
          {consulta.isLoading && <p className="py-4 text-center text-slate-500">Carregando…</p>}
          {consulta.data?.pendentes.map((t) => <LinhaTarefa key={t.id} tarefa={t} />)}
          {consulta.data?.pendentes.length === 0 && (
            <p className="py-4 text-center text-slate-500">Nenhuma tarefa pendente.</p>
          )}
        </ul>
      </CardBranco>

      {consulta.data && consulta.data.concluidas.length > 0 && (
        <CardBranco titulo="Concluídas">
          <ul className="space-y-3">
            {consulta.data.concluidas.map((t) => <LinhaTarefa key={t.id} tarefa={t} />)}
          </ul>
        </CardBranco>
      )}
    </>
  )
}

function SecaoLembretes() {
  const { notificar } = useToast()
  const clienteQuery = useQueryClient()
  const [titulo, setTitulo] = useState('')
  const [data, setData] = useState('')
  const [hora, setHora] = useState('')

  const consulta = useQuery({ queryKey: ['lembretes'], queryFn: () => listarLembretes() })
  const invalidar = () => clienteQuery.invalidateQueries({ queryKey: ['lembretes'] })

  const criar = useMutation({
    mutationFn: () => criarLembrete({ titulo: titulo.trim(), data, hora: hora || null }),
    onSuccess: () => {
      setTitulo('')
      setData('')
      setHora('')
      void invalidar()
      notificar('success', 'Lembrete criado com sucesso!')
    },
    onError: () => notificar('error', 'Erro ao criar o lembrete.'),
  })
  const marcarLido = useMutation({
    mutationFn: (id: number) => marcarLembreteLido(id),
    onSuccess: invalidar,
  })
  const excluir = useMutation({
    mutationFn: (id: number) => excluirLembrete(id),
    onSuccess: () => {
      void invalidar()
      notificar('success', 'Lembrete excluído.')
    },
  })

  function aoEnviar(e: FormEvent) {
    e.preventDefault()
    if (titulo.trim() && data) criar.mutate()
  }

  return (
    <CardBranco titulo="Lembretes">
      <form onSubmit={aoEnviar} className="mb-4 space-y-2">
        <input
          value={titulo}
          onChange={(e) => setTitulo(e.target.value)}
          placeholder="Título do lembrete…"
          className="w-full rounded-md border border-gray-300 px-3 py-2 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
        />
        <div className="flex gap-2">
          <input
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="flex-1 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
          />
          <input
            type="time"
            value={hora}
            onChange={(e) => setHora(e.target.value)}
            className="w-28 rounded-md border border-gray-300 px-3 py-2 text-sm dark:border-dark-border dark:bg-dark-bg dark:text-gray-100"
          />
          <button
            type="submit"
            disabled={criar.isPending || !titulo.trim() || !data}
            className="rounded-md bg-indigo-600 px-4 py-2 text-sm font-bold text-white hover:bg-indigo-700 disabled:opacity-50"
          >
            Criar
          </button>
        </div>
      </form>

      <ul className="space-y-3">
        {consulta.data?.lembretes.map((l) => (
          <li
            key={l.id}
            className="flex items-center justify-between rounded-lg border p-3 hover:bg-indigo-50 dark:border-dark-border dark:hover:bg-dark-hover"
          >
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{l.titulo}</p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                {l.data_lembrete}
                {l.hora_lembrete ? ` às ${l.hora_lembrete.slice(0, 5)}` : ''}
              </p>
            </div>
            <div className="flex gap-3">
              <button
                onClick={() => marcarLido.mutate(l.id)}
                className="text-xs font-medium text-emerald-600 hover:text-emerald-700"
              >
                Lido
              </button>
              <button
                onClick={() => excluir.mutate(l.id)}
                className="text-red-500 hover:text-red-700"
                aria-label={`Excluir ${l.titulo}`}
              >
                <FontAwesomeIcon icon={faTrashAlt} />
              </button>
            </div>
          </li>
        ))}
        {consulta.data?.lembretes.length === 0 && (
          <p className="py-4 text-center text-slate-500">Nenhum lembrete pendente.</p>
        )}
      </ul>
    </CardBranco>
  )
}

export function TarefasPage() {
  return (
    <div className="mx-auto max-w-4xl">
      <div className="mb-8">
        <h1 className="mb-2 text-3xl font-bold text-slate-800 dark:text-slate-100">Minhas Tarefas</h1>
        <p className="text-slate-500 dark:text-slate-400">
          Organize seu fluxo de trabalho e pendências.
        </p>
      </div>
      <SecaoTarefas />
      <SecaoLembretes />
    </div>
  )
}
